import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import StripesBackground from '../components/StripesBackground';
import { projectsAPI } from '../utils/api';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Helper function to get the correct URL (for Cloudinary or local files)
  const getMediaUrl = (url) => {
    if (!url) return null;
    // If URL starts with http:// or https://, it's already a complete URL
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Otherwise, it's a relative path, prepend BACKEND_URL
    return `${BACKEND_URL}${url}`;
  };

  useEffect(() => {
    loadProject();
  }, [id]);

  const loadProject = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getOne(id);
      setProject(response.data);
    } catch (error) {
      console.error('Failed to load project:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <p className="text-gray-500">Loading project...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Project not found</p>
          <Link to="/projects" className="text-blue-600 hover:underline">Back to Projects</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="project-detail-page">
      <div className="relative py-16">
        <StripesBackground />
        
        <div className="container mx-auto px-6 relative z-10 max-w-5xl">
          {/* Back Link */}
          <Link 
            to="/projects" 
            className="inline-flex items-center text-gray-600 hover:text-black mb-8 transition-colors"
            data-testid="back-to-projects"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Projects
          </Link>

          {/* Project Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-black mb-4" data-testid="project-detail-title">{project.title}</h1>
            <div className="flex flex-wrap gap-4 text-gray-600">
              <span className="font-semibold" data-testid="project-detail-role">{project.role}</span>
              <span>•</span>
              <span data-testid="project-detail-company">{project.company}</span>
              <span>•</span>
              <span data-testid="project-detail-year">{project.year}</span>
              <span>•</span>
              <span className="text-sm bg-gray-100 px-3 py-1 rounded" data-testid="project-detail-category">{project.category}</span>
            </div>
          </div>

          {/* Main Image */}
          <div className="mb-8">
            <img
              src={getMediaUrl(project.main_image)}
              alt={project.title}
              className="w-full rounded-lg shadow-lg"
              data-testid="project-detail-main-image"
            />
          </div>

          {/* Description */}
          {project.description && (
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-black mb-4">About</h2>
              <div 
                className="text-gray-700 leading-relaxed prose prose-lg max-w-none" 
                data-testid="project-detail-description"
                dangerouslySetInnerHTML={{ __html: project.description }}
              />
            </div>
          )}

          {/* Video */}
          {project.video_url && (
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-black mb-4">Video</h2>
              <div className="bg-black rounded-lg p-4">
                <video
                  controls
                  className="w-full rounded"
                  data-testid="project-detail-video"
                >
                  <source src={getMediaUrl(project.video_url)} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          )}

          {/* Extra Images */}
          {project.extra_images && project.extra_images.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-black mb-4">Gallery</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {project.extra_images.map((img, index) => (
                  <img
                    key={index}
                    src={getMediaUrl(img)}
                    alt={`${project.title} - Image ${index + 1}`}
                    className="w-full rounded-lg shadow-md"
                    data-testid={`project-detail-extra-image-${index}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
